import React, { useState } from 'react';
import { useWebCategories } from '../../hooks/useWebCategories';
import { Category } from '../../types/category';
import {
  GlobeAltIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';
import WebCategoryForm from './WebCategoryForm';

const WebCategoryList: React.FC = () => {
  const { mainCategories, loading, error, deleteCategory, refreshCategories } = useWebCategories();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  const handleDeleteConfirm = async () => {
    if (categoryToDelete) {
      try {
        await deleteCategory(categoryToDelete.id);
        setIsConfirmDeleteOpen(false);
        setCategoryToDelete(null);
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setIsModalOpen(true);
  };

  const handleDelete = (category: Category) => {
    setCategoryToDelete(category);
    setIsConfirmDeleteOpen(true);
  };

  const handleAdd = () => {
    setSelectedCategory(null);
    setIsModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Hiba: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          {mainCategories.length} főkategória
        </div>
        <button
          onClick={handleAdd}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Új kategória
        </button>
      </div>

      {/* Categories List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <ul className="divide-y divide-gray-200">
          {mainCategories.length === 0 ? (
            <li className="px-6 py-12 text-center text-gray-500">
              <GlobeAltIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2">Még nincsenek webshop kategóriák</p>
              <p className="text-sm mt-1">Kattints az "Új kategória" gombra a kezdéshez</p>
            </li>
          ) : (
            mainCategories.map((category) => (
              <li key={category.id}>
                {/* Main Category */}
                <div className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FolderIcon className="h-6 w-6 text-blue-500" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{category.name}</h3>
                        <p className="text-sm text-gray-500">
                          {category.subcategories?.length || 0} alkategória
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(category)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Subcategories */}
                {category.subcategories && category.subcategories.length > 0 && (
                  <div className="bg-gray-50 px-6 py-2">
                    <ul className="space-y-2">
                      {category.subcategories.map((subcat) => (
                        <li key={subcat.id} className="flex items-center justify-between py-2">
                          <div className="flex items-center space-x-3 ml-8">
                            <div className="w-4 h-4 border-l-2 border-b-2 border-gray-300"></div>
                            <span className="text-gray-700">{subcat.name}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEdit(subcat)}
                              className="p-1 text-blue-600 hover:bg-blue-50 rounded-md"
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(subcat)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded-md"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Modals */}
      {isModalOpen && (
        <WebCategoryForm
          category={selectedCategory}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedCategory(null);
          }}
          onSuccess={() => {
            refreshCategories();
          }}
        />
      )}

      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Kategória törlése"
        deleteMessage={`Biztosan törölni szeretnéd a(z) "${categoryToDelete?.name}" kategóriát?`}
        deleteWarning="Ez a művelet nem vonható vissza."
      />
    </div>
  );
};

export default WebCategoryList;
